import moment from 'moment';
import { userBans } from '@shopmy/universal';
import { ADMIN_SCOPES } from '../constants/admin_scope_constants';

const { getUserBanCategoryLabel } = userBans;

/**
 * function that parses the db user table "social_links" column and searches for common
 * social media links inside of it. then it turns the links found into an object that holds
 * each link individually listed
 *
 * @param {string} social_links comma separated list of a user's social links
 * @returns object with each social individually listed
 */
export const splitDbSocialLinksIntoIndividualLinks = social_links => {
  if (!social_links) return {};

  const links = {
    youtube_url: null,
    instagram_url: null,
    tiktok_url: null,
    twitter_url: null,
    facebook_url: null,
    website_url: null
  };

  const youtube_regex = /.*(youtube|youtu\.be).*/gi;
  const instagram_regex = /.*(instagram).*/gi;
  const tiktok_regex = /.*(tiktok).*/gi;
  const twitter_regex = /.*(twitter).*/gi;
  const facebook_regex = /.*(facebook).*/gi;

  if (!social_links) return links;
  const split_links = social_links.split(',');

  for (let link of split_links) {
    switch (true) {
      case link.match(youtube_regex) !== null:
        links.youtube_url = link;
        break;
      case link.match(instagram_regex) !== null:
        links.instagram_url = link;
        break;
      case link.match(tiktok_regex) !== null:
        links.tiktok_url = link;
        break;
      case link.match(twitter_regex) !== null:
        links.twitter_url = link;
        break;
      case link.match(facebook_regex) !== null:
        links.facebook_url = link;
        break;
      default:
        // because user could have multiple other websites, append to array and add all
        if (!links.website_url) links.website_url = [link];
        else links.website_url.push(link);
    }
  }

  return links;
};

/**
 * User type detection helpers
 */
export const isBrand = user => !!user.Brand_id;

export const isShopper = user => !!user.isShopper;

export const isCreator = user => !isBrand(user) && !isShopper(user) && !user.isManager && !user.isPublisher;

// Check if user isAdmin and has some admin scopes
export const isAdmin = user => !!user.isAdmin && user.admin_scopes?.scopes?.length > 0;

// Check if current admin user has the 'all' (Super Admin) scope
export const isSuperAdmin = currentUser => !!currentUser?.scopes?.includes('all');

/**
 * Get all user types (can be multiple)
 * @param {Object} user - User object
 * @returns {string[]} - Array of types like ['admin', 'creator']
 */
export const getUserTypes = user => {
  const types = [];

  if (isAdmin(user)) types.push('admin');
  if (isBrand(user)) types.push('brand');
  if (isShopper(user)) types.push('shopper');
  if (isCreator(user)) types.push('creator');

  return types.length > 0 ? types : ['user'];
};

/**
 * Get preset labels that match a user's admin scopes.
 * A preset matches if ALL of its scopes are present in the user's scope list.
 */
export const getPresetsForUser = (user, presets) => {
  const scopeString = user?.admin_scopes?.scopes || '';
  if (!scopeString || !presets?.length) return [];
  const scopeList = scopeString.split(',').filter(Boolean);

  if (scopeList.includes(ADMIN_SCOPES.ALL)) {
    return presets.filter(p => p.scopes.includes(ADMIN_SCOPES.ALL)).map(p => p.label);
  }

  return presets
    .filter(p => !p.scopes.includes(ADMIN_SCOPES.ALL))
    .filter(p => p.scopes.every(s => scopeList.includes(s)))
    .map(p => p.label);
};

/**
 * Get display data for user type badges (can show multiple)
 */
export const getUserTypeBadges = user => {
  const types = getUserTypes(user);

  const badgeMap = {
    admin: { display: 'ADMIN', className: 'admin' },
    brand: { display: 'BRAND', className: 'brand' },
    shopper: { display: 'SHOPPER', className: 'shopper' },
    creator: { display: 'CREATOR', className: 'creator' },
    user: { display: 'USER', className: 'user' }
  };

  return types.map(type => badgeMap[type]);
};

export const getBanEventSummary = banEvent =>
  banEvent.action === 'ban' ? getUserBanCategoryLabel(banEvent.category) : `Unbanned ${moment(banEvent.createdAt).format('MMM D, YYYY')}`;

// One line per ban or unban, newest first — the row cell's hover title.
export const getBanHistoryTooltip = (banEvents = []) =>
  banEvents
    .map(banEvent => {
      const verb = banEvent.action === 'ban' ? `Banned for ${getUserBanCategoryLabel(banEvent.category)}` : 'Unbanned';
      const actor = banEvent.adminName || banEvent.source;
      const note = banEvent.note ? ` — "${banEvent.note}"` : '';
      return `${moment(banEvent.createdAt).format('MMM D, YYYY')}: ${verb} by ${actor}${note}`;
    })
    .join('\n');
